import { doc, getDoc } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { Image, SendHorizontal, ThumbsUp } from 'lucide-react';
import React, { ChangeEvent, ClipboardEvent, useCallback, useEffect, useRef, useState, useTransition } from 'react';

import { ChatInput } from './chat-input.ui';
import { EmojiPicker } from './emoji-picker.ui';
import { ImageUpload } from './image-upload.ui';

import { ChatData, FilesWithId, ImageData, ImagesData } from '@/slw/entities/chat';
import { useChatStore } from '@/slw/shared/lib/firebase/chat-store';
import { db, markMessageAsSeen, sendImages, sendMessage } from '@/slw/shared/lib/firebase/utils';
import { cn, isValidImage } from '@/slw/shared/lib/utils';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { Button, buttonVariants } from '@/slw/shared/ui/button';

interface ChatBottomBarProps {
  isMobile: boolean;
  openModal: (data: ImageData) => void;
  scrollToBottom: () => void;
}

export const BottomBarIcons = [{ icon: Image }];

export default function ChatBottomBar({ openModal, scrollToBottom }: ChatBottomBarProps) {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { chatId, user: receiverUser } = useChatStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPending, startTransition] = useTransition();
  const [selectedImages, setSelectedImages] = useState<FilesWithId>([]);
  const { user: currentUser } = useAuthStore();
  const [imagesPreview, setImagesPreview] = useState<ImagesData>([]);
  const isEnterKeyPressed = useRef(false);

  useEffect(() => () => imagesPreview.forEach(({ src }) => URL.revokeObjectURL(src)), [imagesPreview]);

  const cleanImages = useCallback(() => {
    setSelectedImages([]);
    setImagesPreview([]);
    imagesPreview.forEach(({ src }) => URL.revokeObjectURL(src));
  }, [imagesPreview]);

  useEffect(() => {
    if (!currentUser.id) {
      cleanImages();
      setMessage('');
    }
  }, [cleanImages, currentUser.id]);

  // Update isSeen status when chat input is focused
  const updateSeenStatus = useCallback(async () => {
    if (!currentUser.id || !chatId) return;

    try {
      await markMessageAsSeen(chatId, currentUser.id);
    } catch (err) {
      // Error updating seen status
    }
  }, [currentUser.id, chatId]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  // const sendMessage = (newMessage: Message) => {
  //   useChatStore.setState((state) => ({
  //     messages: [...state.messages, newMessage],
  //   }));
  // };

  const handleThumbsUp = () => {
    addMessage('👍');
  };

  const handleSend = () => {
    const trimmedValue = message.trim();

    if (imagesPreview.length > 0) {
      startTransition(async () => {
        if (!currentUser.id || !receiverUser) return;

        const userDocRef = doc(db, 'users', currentUser.id!);
        const userDocSnap = await getDoc(userDocRef);

        const user = userDocSnap.data() as ChatData['user'];

        await sendImages({
          text: trimmedValue || null,
          selectedImages,
          chatId: chatId!,
          sender: user,
          receiver: receiverUser,
        });

        setMessage('');
        cleanImages();

        if (inputRef.current) {
          inputRef.current.focus();
        }

        scrollToBottom();
      });
    } else if (trimmedValue) {
      addMessage(trimmedValue);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeImage = (targetId: number) => {
    setSelectedImages(selectedImages.filter(({ id }) => id !== targetId));
    setImagesPreview(imagesPreview.filter(({ id }) => id !== targetId));

    const imageToRemove = imagesPreview.find(({ id }) => id === targetId);

    if (imageToRemove?.src) {
      URL.revokeObjectURL(imageToRemove.src);
    }
  };

  // const formattedTime = new Date().toLocaleTimeString('en-US', {
  //   hour: 'numeric',
  //   minute: '2-digit',
  //   hour12: true,
  // });

  const addMessage = async (text: string): Promise<void> => {
    startTransition(async () => {
      if (receiverUser) {
        const userDocRef = doc(db, 'users', currentUser.id!);
        const userDocSnap = await getDoc(userDocRef);

        const user = userDocSnap.data() as ChatData['user'];

        await sendMessage({
          text,
          imageData: null,
          chatId: chatId!,
          sender: user,
          receiver: receiverUser,
        });

        setMessage('');

        if (inputRef.current) {
          inputRef.current.focus();
        }

        scrollToBottom();
      }
    });
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement> | ClipboardEvent<HTMLTextAreaElement>) => {
    if (!currentUser.id) return;

    const files = 'clipboardData' in e ? e.clipboardData.files : e.target.files;

    if (!files || !files.length) return;

    const rawImages = [...files].filter(({ name, size }) => isValidImage(name, size));

    if (rawImages.length === 0) return;

    const imagesId = rawImages.map((_, index) => Math.floor(Date.now() + Math.random() + index));

    // Nếu là từ file input, gửi ngay lập tức
    if (!('clipboardData' in e)) {
      startTransition(async () => {
        if (!receiverUser) return;

        const userDocRef = doc(db, 'users', currentUser.id!);
        const userDocSnap = await getDoc(userDocRef);

        const user = userDocSnap.data() as ChatData['user'];

        await sendImages({
          text: null,
          selectedImages: rawImages.map((image, index) => Object.assign(image, { id: imagesId[index] })),
          chatId: chatId!,
          sender: user,
          receiver: receiverUser,
        });

        if (inputRef.current) {
          inputRef.current.focus();
        }

        scrollToBottom();
      });
    } else {
      // Nếu là từ paste, tạo preview

      const imagesWithId = rawImages.map((image, index) => Object.assign(image, { id: imagesId[index] }));
      const imagesData = rawImages.map((image, index) => ({
        id: imagesId[index],
        src: URL.createObjectURL(image),
        alt: image.name,
      }));

      setSelectedImages([...selectedImages, ...imagesWithId]);
      setImagesPreview([...imagesPreview, ...imagesData]);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();

      if (isEnterKeyPressed.current) return;

      isEnterKeyPressed.current = true;

      setTimeout(() => {
        isEnterKeyPressed.current = false;
      }, 100);

      handleSend();
    }

    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      setMessage((prev) => prev + '\n');
    }
  };

  const isDisabled = !message.trim() && imagesPreview.length === 0;

  return (
    <div className='px-2 py-4 flex flex-col w-full gap-2 relative'>
      <AnimatePresence>
        {imagesPreview.length > 0 && (
          <div className='w-full'>
            <ImageUpload imagesPreview={imagesPreview} openModal={openModal} removeImage={removeImage} />
          </div>
        )}
      </AnimatePresence>
      <div className='flex justify-between items-center w-full'>
        <div className='flex'>
          {/* <Popover>
            <PopoverTrigger asChild>
              <Link
                href='#'
                className={cn(
                  buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }), 'h-9 w-9', 'shrink-0'
                )}
              >
                <PlusCircle size={22} className='text-muted-foreground' />
              </Link>
            </PopoverTrigger>
            <PopoverContent side='top' className='w-full p-2'>
              {message.trim() || isMobile ? (
                <div className='flex gap-2'>
                  <Link
                    href='#'
                    className={cn(
                      buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }),
                      'h-9 w-9',
                      'shrink-0',
                    )}
                  >
                    <Mic size={22} className='text-muted-foreground' />
                  </Link>
                  {BottomBarIcons.map((icon, index) => (
                    <div key={index}>
                      {icon.icon === FileImage ? (
                        <>
                          <input
                            type='file'
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept='image/*'
                            style={{ display: 'none' }}
                          />
                          <Button
                            onClick={triggerFileInput}
                            className={cn(
                              buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }),
                              'h-9 w-9',
                              'shrink-0',
                            )}
                          >
                            <FileImage size={22} className='text-muted-foreground' />
                          </Button>
                        </>
                      ) : (
                        <Link
                          href='#'
                          className={cn(
                            buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }),
                            'h-9 w-9',
                            'shrink-0',
                          )}
                        >
                          <icon.icon size={22} className='text-muted-foreground' />
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Link
                  href='#'
                  className={cn(
                    buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }),
                    'h-9 w-9',
                    'shrink-0',
                  )}
                >
                  <Mic size={22} className='text-muted-foreground' />
                </Link>
              )}
            </PopoverContent>
          </Popover> */}
          <div className='flex'>
            {BottomBarIcons.map((icon, index) => (
              <div key={index}>
                {icon.icon === Image ? (
                  <>
                    <input
                      type='file'
                      ref={fileInputRef}
                      multiple
                      onChange={handleImageUpload}
                      accept='image/*'
                      style={{ display: 'none' }}
                    />
                    <Button
                      disabled={isPending}
                      onClick={triggerFileInput}
                      size='icon'
                      color='light'
                      className='flex items-center justify-center'
                    >
                      <Image size={22} className='text-muted-foreground' />
                    </Button>
                  </>
                ) : (
                  <Button size='icon' className='flex items-center justify-center'>
                    <icon.icon size={22} className='text-muted-foreground' />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence initial={false}>
          <motion.div
            key='input'
            className='w-full relative'
            layout
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{
              opacity: { duration: 0.05 },
              layout: {
                type: 'spring',
                bounce: 0.15,
              },
            }}
          >
            <ChatInput
              value={message}
              ref={inputRef}
              onKeyDown={handleKeyPress}
              onChange={handleInputChange}
              onFocus={updateSeenStatus}
              onPaste={handleImageUpload}
              placeholder='Enter your message'
              className='rounded-full'
              disabled={!currentUser.id}
            />
            <div className={cn('absolute right-4 bottom-2', isPending ? 'opacity-50 cursor-not-allowed' : '')}>
              <EmojiPicker
                onChange={(value) => {
                  setMessage(message + value);

                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
              />
            </div>
          </motion.div>

          {message.trim() || imagesPreview.length > 0 ? (
            <Button
              className='h-9 w-9 shrink-0'
              onClick={handleSend}
              disabled={!currentUser.id || isDisabled || isPending}
              variant='ghost'
              size='icon'
              color='light'
            >
              <SendHorizontal size={22} className='text-muted-foreground' />
            </Button>
          ) : (
            <Button
              className={cn(buttonVariants({ variant: 'ghost', color: 'light', size: 'icon' }), 'h-9 w-9 shrink-0')}
              onClick={handleThumbsUp}
              disabled={isPending}
              variant='ghost'
              size='icon'
            >
              <ThumbsUp size={22} className='text-muted-foreground' />
            </Button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
